/**
 * Commitment terms service — client surface for stopping rules (WS-A).
 *
 * PRD: docs/9-exploration/PRD-scorecard-to-strongest.md.
 * Schema + RPCs: supabase/migrations/680_plan_commitment_terms.sql.
 * Judgment (state machine mirror, overdue, labels): src/lib/commitmentTerms.ts.
 *
 * All writes go through the SECURITY DEFINER RPCs (Build Contract §2.3a) —
 * this module never inserts or updates term rows directly. Reads go through
 * the typed client against the commitment_terms_check view so contract drift
 * is a compile error. The RPCs raise on refusal (planning-layer error style);
 * refusals surface here as { ok: false, error } with the database's own
 * sentence, which is already written in user language.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { Database } from '../types/database.generated';
import type { TermKind, TermResolution } from './commitmentTerms';

export type CommitmentTermCheckRow =
  Database['public']['Views']['commitment_terms_check']['Row'];

export interface TermWriteResult {
  ok: boolean;
  termId?: string;
  error?: string;
}

function toResult(data: unknown, error: { message: string } | null): TermWriteResult {
  if (error) return { ok: false, error: error.message };
  return { ok: true, termId: typeof data === 'string' ? data : undefined };
}

export async function listTermsForCycle(
  planCycleId: string
): Promise<{ rows: CommitmentTermCheckRow[]; error?: string }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('commitment_terms_check')
    .select('*')
    .eq('plan_cycle_id', planCycleId)
    .order('created_at', { ascending: true });
  if (error) return { rows: [], error: error.message };
  return { rows: data ?? [] };
}

/** Open terms (armed or tripped) across a workspace — the review queue feed. */
export async function listOpenTermsForWorkspace(
  workspaceId: string
): Promise<{ rows: CommitmentTermCheckRow[]; error?: string }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('commitment_terms_check')
    .select('*')
    .eq('workspace_id', workspaceId)
    .in('status', ['armed', 'tripped'])
    .order('review_by', { ascending: true, nullsFirst: false });
  if (error) return { rows: [], error: error.message };
  return { rows: data ?? [] };
}

export interface AttachTermInput {
  planCycleId: string;
  itemId: string;
  kind: TermKind;
  conditionText: string;
  /** ISO date (YYYY-MM-DD). Required for kind='expiry'. */
  reviewBy?: string;
  metricRef?: Record<string, unknown>;
}

export async function attachTerm(input: AttachTermInput): Promise<TermWriteResult> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('attach_commitment_term', {
    p_plan_cycle_id: input.planCycleId,
    p_item_id: input.itemId,
    p_kind: input.kind,
    p_condition_text: input.conditionText,
    p_metric_ref: (input.metricRef ?? undefined) as
      | Database['public']['Tables']['plan_commitment_terms']['Row']['metric_ref']
      | undefined,
    p_review_by: input.reviewBy ?? undefined,
  });
  return toResult(data, error);
}

export async function tripTerm(termId: string, basis: string): Promise<TermWriteResult> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('trip_commitment_term', {
    p_term_id: termId,
    p_basis: basis,
  });
  return toResult(data, error);
}

export async function resolveTerm(
  termId: string,
  resolution: TermResolution,
  basis: string,
  decisionId?: string
): Promise<TermWriteResult> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('resolve_commitment_term', {
    p_term_id: termId,
    p_resolution: resolution,
    p_basis: basis,
    p_decision_id: decisionId ?? undefined,
  });
  return toResult(data, error);
}

export async function withdrawTerm(termId: string, basis: string): Promise<TermWriteResult> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('withdraw_commitment_term', {
    p_term_id: termId,
    p_basis: basis,
  });
  return toResult(data, error);
}
